import React from "react"
import { Field, Fields } from "redux-form"
import {
  Box,
  Divider,
  InputAdornment,
  MenuItem,
  Typography
} from "@material-ui/core"
import { makeStyles } from "@material-ui/core/styles"
import { ToggleButton } from "@material-ui/lab"
import { useSelector } from "react-redux"
import FieldCurrency from "../../component/forms/FieldCurrency"
import FieldPercent from "../../component/forms/FieldPercent"
import FieldsAddress from "../../component/forms/FieldsAddress"
import FieldTextarea from "../../component/forms/FieldTextarea"
import FieldDatePicker from "../../component/forms/FieldDatePicker"
import SelectInput from "../../component/forms/SelectInput"
import FieldScale from "../../component/forms/FieldScale"
import CountrySelect from "../../component/select/CountrySelect"
import { formsAction } from "../../i18n/lstrings"
import {
  CustomFieldPropTypes,
  UserFormElementPropTypes
} from "./CustomFormPropTypes"
import FieldList from "../../component/forms/FieldList"
import FieldMatrix from "../../component/forms/FieldMatrix"
import useSmartFormat from "../../i18n/useSmartFormat"
import RequiredStar from "./RequiredStar"
import FormElementVerticalSeparator from "./elements/FormElementVerticalSeparator"
import { FORM_ADDABLE_ELEMENTS_TYPES } from "../admin/forms/AdminFormCreationAddableElements"

const useFieldStyle = makeStyles((theme) => ({
  field: {
    maxWidth: "inherit",
    width: "100%"
  },
  keepCrlf: {
    whiteSpace: "pre"
  },
  flagIcon: {
    marginRight: theme.spacing(2)
  }
}))

function CustomFieldBox(props) {
  const { elementNumber, element, children } = props
  const { title, description, required } = element
  const realTitle = elementNumber ? `${elementNumber}. ${title}` : `${title}`
  const classes = useFieldStyle()
  return (
    <Box p={1} marginTop={1}>
      <Typography variant="h6" id="element-title">
        {realTitle} {required && <RequiredStar />}
      </Typography>

      <Typography id="element-description" className={classes.keepCrlf}>
        {description}
      </Typography>
      {children}
    </Box>
  )
}

CustomFieldBox.propTypes = CustomFieldPropTypes.isRequired

function CustomTextField(props) {
  const { element, onBlur } = props
  const classes = useFieldStyle()
  const { data } = element
  const placeholder = data?.placeholder?.value || ""

  return (
    <CustomFieldBox {...props}>
      <Field
        onBlur={onBlur}
        component={FieldTextarea}
        id={element.id}
        label=""
        name={element.id}
        placeholder={placeholder}
        type={element.type}
        className={classes.field}
      />
    </CustomFieldBox>
  )
}

CustomTextField.propTypes = CustomFieldPropTypes.isRequired

function CustomCountryField(props) {
  const { element, onBlur } = props
  const classes = useFieldStyle()

  if (!element) {
    return null
  }

  return (
    <CustomFieldBox {...props}>
      <Field
        id={element.id}
        name={element.id}
        onBlur={onBlur}
        component={CountrySelect}
        className={classes.field}
      />
    </CustomFieldBox>
  )
}

CustomCountryField.propTypes = CustomFieldPropTypes.isRequired

const renderAddressFields = (props) => {
  const { element, onBlur, ...fields } = props
  return (
    <CustomFieldBox {...props}>
      <FieldsAddress fields={fields} element={element} onBlur={onBlur} />
    </CustomFieldBox>
  )
}

function CustomAddressField(props) {
  const { element } = props
  const { id } = element

  return (
    <Fields
      names={[
        `${id}.address`,
        `${id}.city`,
        `${id}.postalCode`,
        `${id}.country`
      ]}
      component={renderAddressFields}
      {...props}
    />
  )
}

CustomAddressField.propTypes = CustomFieldPropTypes.isRequired

function CustomDateField(props) {
  const { element, onBlur } = props
  const classes = useFieldStyle()
  const { minDate, maxDate } = element.data

  return (
    <CustomFieldBox {...props}>
      <Field
        component={FieldDatePicker}
        id={element.id}
        name={element.id}
        onBlur={onBlur}
        className={classes.field}
        minDate={minDate && minDate.value}
        maxDate={maxDate && maxDate.value}
      />
    </CustomFieldBox>
  )
}

CustomDateField.propTypes = CustomFieldPropTypes.isRequired

function CustomPercentField(props) {
  const { element, onBlur } = props
  const classes = useFieldStyle()

  return (
    <CustomFieldBox {...props}>
      <Field
        component={FieldPercent}
        id={element.id}
        label=""
        name={element.id}
        type={element.type}
        onBlur={onBlur}
        className={classes.field}
        normalize={(v) => (!v ? 0.0 : parseFloat(v) / 100.0)}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <Typography>%</Typography>
            </InputAdornment>
          )
        }}
      />
    </CustomFieldBox>
  )
}

CustomPercentField.propTypes = CustomFieldPropTypes.isRequired

function CustomSelectField(props) {
  const { element, onBlur } = props
  const classes = useFieldStyle()
  const format = useSmartFormat()
  const list = element?.data?.list?.value || {}
  const { jumpIdentifier, jumpId, values } = list
  const defaultSelectString = format(formsAction.defaultFormSelectLabel)
  const initialValues = useSelector((state) => state.userForm.initialValuesUser)

  if (!jumpId || !values) {
    return null
  }

  return (
    <CustomFieldBox {...props}>
      <Field
        id={element.id}
        name={element.id}
        onBlur={onBlur}
        component={SelectInput}
        className={classes.field}
        format={(value = {}) => value.selected}
        normalize={(value) => ({
          jumpIdentifier,
          values, // We need this in backend
          selected: value
        })}
        input={{ defaultValue: initialValues[element.id] }}
      >
        <MenuItem value="" disabled>
          {defaultSelectString}
        </MenuItem>
        {values.map((item) => (
          <MenuItem key={item.key} value={item.key}>
            {item.value}
          </MenuItem>
        ))}
      </Field>
    </CustomFieldBox>
  )
}

CustomSelectField.propTypes = CustomFieldPropTypes.isRequired

function CustomCurrencyField(props) {
  const { element, onBlur } = props
  const classes = useFieldStyle()
  const defaultCurrency = element?.data?.currency?.value // We don't have to worry about 'undefined' here because <CurrencySelect /> will take care of it

  return (
    <CustomFieldBox {...props}>
      <Field
        component={FieldCurrency}
        id={element.id}
        label=""
        onBlur={onBlur}
        name={element.id}
        type="number"
        className={classes.field}
        defaultCurrency={defaultCurrency}
      />
    </CustomFieldBox>
  )
}

CustomCurrencyField.propTypes = CustomFieldPropTypes.isRequired

function CustomListField(props) {
  const { element, onBlur } = props
  const classes = useFieldStyle()

  const items = element?.data?.list?.value ?? []
  const multiSelection = element.type !== "monoSelection"

  return (
    <CustomFieldBox {...props}>
      <Field
        component={FieldList}
        id={element.id}
        name={element.id}
        onBlur={onBlur}
        className={classes.field}
        items={items}
        multiSelection={multiSelection}
      />
    </CustomFieldBox>
  )
}

CustomListField.propTypes = CustomFieldPropTypes.isRequired

function CustomScaleField(props) {
  const { element, onBlur } = props
  const classes = useFieldStyle()

  const scaleProps = {
    leftText: element.data.leftText.value,
    middleText: element.data.middleText.value,
    rightText: element.data.rightText.value,
    showLabels: element.data.showLabels.value,
    maxScale: element.data.maxScale.value,
    startAtOne: element.data.startAtOne.value
  }

  return (
    <CustomFieldBox {...props}>
      <Field
        component={FieldScale}
        id={element.id}
        name={element.id}
        onBlur={onBlur}
        type={element.type}
        className={classes.field}
      >
        {Array.from(
          { length: scaleProps.maxScale },
          (_, i) => i + scaleProps.startAtOne // startAtOne is a boolean
        ).map((item) => (
          <ToggleButton key={item} value={item}>
            <Typography variant="h3">{item}</Typography>
          </ToggleButton>
        ))}
      </Field>
      {scaleProps.showLabels && (
        <Box display="flex" justifyContent="space-evenly">
          <Typography>{scaleProps.leftText}</Typography>
          <Typography>{scaleProps.middleText}</Typography>
          <Typography>{scaleProps.rightText}</Typography>
        </Box>
      )}
    </CustomFieldBox>
  )
}

CustomScaleField.propTypes = CustomFieldPropTypes.isRequired

function HorizontalSeparator() {
  return <Divider />
}

function VerticalSeparator() {
  return (
    <Box height="100%" p={2}>
      <FormElementVerticalSeparator />
    </Box>
  )
}

function CustomFreeText(props) {
  const { element } = props
  const { freeText } = element.data

  return (
    <CustomFieldBox {...props}>
      <div dangerouslySetInnerHTML={{ __html: freeText }} />
    </CustomFieldBox>
  )
}

CustomFreeText.propTypes = CustomFieldPropTypes.isRequired

function CustomMatrixField(props) {
  const { element, onBlur } = props
  const classes = useFieldStyle()
  const { initialValue, selection, monoSelection } = element.data
  const typeSelection = monoSelection
    ? ["unique"]
    : selection?.list.filter((x) => x.value === true).map((x) => x.name)

  return (
    <CustomFieldBox {...props}>
      <Field
        component={FieldMatrix}
        id={element.id}
        onBlur={onBlur}
        name={element.id}
        className={classes.field}
        initialValue={initialValue}
        typeSelection={typeSelection}
      />
    </CustomFieldBox>
  )
}

CustomMatrixField.propTypes = CustomFieldPropTypes.isRequired

const fieldMap = {
  text: CustomTextField,
  address: CustomAddressField,
  phone: CustomTextField,
  country: CustomCountryField,
  mail: CustomTextField,
  date: CustomDateField,
  list: CustomSelectField,
  monoSelection: CustomListField,
  multiSelection: CustomListField,
  currency_value: CustomCurrencyField,
  scale: CustomScaleField,
  number: CustomTextField,
  percent: CustomPercentField,
  separator: HorizontalSeparator,
  freeText: CustomFreeText,
  matrixSelection: CustomMatrixField
}

/**
 * Renders the form element in a form to be filled out.
 * @param {Boolean} isSibling
 * @param element
 * @param onBlur
 * @param {Number} elementNumber - the number to display next to the element's title
 * @returns {JSX.Element|null}
 * @constructor
 */
export default function UserFormElement({
  isSibling,
  element,
  elementNumber,
  onBlur
}) {
  const { type } = element
  if (type === FORM_ADDABLE_ELEMENTS_TYPES.SEPARATOR) {
    const SeparatorElement = isSibling ? VerticalSeparator : HorizontalSeparator
    return <SeparatorElement />
  }

  const FoundField = fieldMap[type]
  if (!FoundField) {
    // eslint-disable-next-line no-console
    console.warn(`Type ${type} is not supported yet`)
    return null
  }

  return (
    <FoundField
      elementNumber={elementNumber}
      element={element}
      onBlur={onBlur}
    />
  )
}

UserFormElement.propTypes = UserFormElementPropTypes.isRequired
